/**
 * Toaster primitive.
 *
 * Transient, auto-dismissing status notifications wrapped over `sonner`
 * (D-166 — proto parity). Sonner owns the headless behaviour: the portal
 * host, the dismiss timers, stacking and collapse of multiple toasts,
 * swipe- and click-to-dismiss, pause-on-hover / pause-on-window-blur, the
 * `aria-live` region and its focus hotkey, and `prefers-reduced-motion`
 * handling. Our job is to pin token-backed styling onto sonner's markup and
 * to fix the proto's placement — we add no behaviour of our own.
 *
 * Shape differs from `Dialog.tsx` deliberately: sonner is
 * not a compound component. There is exactly one host element (`Toaster`)
 * plus an imperative `toast` handle, so this module exports those two and
 * no aggregate object. `toast` is **re-exported here on purpose** so that
 * no route or component imports `sonner` directly: the wrapper stays the
 * single place the vendor is named, which is both the house convention for
 * third-party UI and the seam tests mock.
 *
 * ## Who mounts it
 *
 * NOT `AppProviders` / `Composition.tsx`, and NOT `shell/AdminCmsLayout.tsx`
 * — both sit in the member entry graph, so mounting there would ship this
 * admin-only dependency to every member. The first consumer mounts it at
 * the right audience boundary instead; today that is `routes/AdminCmsPage.tsx`,
 * behind the `shell/lazyAdminRoutes.ts` code-split seam. The full reasoning
 * lives at that mount site. The first MEMBER-side toast consumer promotes
 * the Toaster to `AppProviders`, one host for the whole tree.
 *
 * ## Why `unstyled: true`
 *
 * Sonner ships its own stylesheet, injected into `document.head` at mount.
 * Its card treatment lives on `[data-sonner-toast][data-styled='true']` —
 * an attribute selector with the same specificity as a class, and injected
 * later than our stylesheet, so plain Tailwind utilities would lose the
 * cascade tie. Upstream shadcn resolves this by raising specificity with
 * `group-[.toaster]:bg-background`-style selectors; those are
 * arbitrary-bracket classes, which FR-047 / `test:architecture` bans. Setting
 * `unstyled: true` removes `data-styled` altogether, so sonner emits no
 * competing card CSS and our token classes apply cleanly with no specificity
 * fight and no bracket escape hatch. Sonner keeps styling the *positioning*
 * container (`[data-sonner-toaster]`), which is layout we do not want to
 * re-author.
 *
 * Styling is token-backed only, matching `Dialog`'s floating-surface
 * treatment (`bg-popover` / `text-popover-foreground` / `border-border`) so a
 * toast reads as the same family of floating chrome. `--color-success` tints
 * the success variant's border — the same token, and the same restraint, the
 * banner this replaced used. No arbitrary-bracket classes (FR-047), no raw
 * colour literals (FR-002).
 *
 * ## The live region, and why `containerAriaLabel` is required
 *
 * Sonner PRE-MOUNTS its live region: the component's single unconditional
 * return renders a `<section aria-live="polite" aria-relevant="additions text"
 * aria-atomic="false">` whether or not anything is queued, and raised toasts
 * are INSERTED into that standing region. (Sonner's own `return null` guard
 * sits inside the position map and suppresses only the inner `<ol>`, which is
 * where `data-sonner-toaster` lives — so an idle host looks empty to an
 * attribute query while the region is genuinely there.) That is the
 * mount-before-content pattern `shell/FeedbackModal` prescribes, so the
 * acknowledgement is announced reliably; `__tests__/Toaster.test.tsx` pins
 * both halves.
 *
 * Because that region is a permanent landmark, its accessible name is
 * user-visible to AT — and sonner's default is the English
 * `"Notifications alt+T"`. `containerAriaLabel` is therefore a REQUIRED prop
 * here, narrowing sonner's optional one: a Finnish-first app (FR-040) must not
 * be able to mount this host and silently ship an English landmark name. The
 * copy is the CONSUMER's to supply — this primitive stays generic and must not
 * reach into a feature i18n namespace (`cms.*`) or import `useTranslate`.
 * Sonner appends its focus hotkey to whatever is passed, so a Finnish label
 * yields "Ilmoitukset alt+T".
 */

import type { ComponentProps } from 'react';
import { Toaster as SonnerToaster, toast } from 'sonner';

// `containerAriaLabel` is optional on sonner's own props; required here so the
// live region's accessible name cannot be left as sonner's English default (see
// the module JSDoc). A type error at the mount site is the cheapest place to
// catch that — cheaper than an a11y review noticing it in production.
export type ToasterProps = Omit<ComponentProps<typeof SonnerToaster>, 'containerAriaLabel'> & {
  readonly containerAriaLabel: string;
};

// The toast card. `w-full` because sonner's container owns the width;
// `items-start` keeps the icon aligned to the first line of a wrapping
// message. Radius/shadow mirror `Dialog.Content`'s floating surface a step
// smaller — a toast is lighter chrome than a modal.
const toastClasses =
  'flex w-full items-start gap-3 rounded-md border p-4 text-sm ' +
  'border-border bg-popover text-popover-foreground shadow-lg';
// Success tints the border only; the surface stays neutral so the copy keeps
// `text-popover-foreground`'s contrast rather than sitting on a wash.
const successToastClasses = 'border-success';
const titleClasses = 'font-medium';
const descriptionClasses = 'text-muted-foreground';
const iconClasses = 'shrink-0 text-success';

/**
 * The single toast host. Placement is the proto's (`position="top-center"`,
 * `offset={16}` — proto `src/App.tsx`); callers may override either via props
 * for a surface that needs different placement, and `toastOptions` merges
 * shallowly so a caller-supplied option set replaces ours wholesale rather
 * than merging per-key — pass the full set if you override it.
 *
 * `containerAriaLabel` is required and has no default here on purpose: the
 * translated copy belongs to the consumer's namespace, not to a primitive.
 */
export function Toaster({ position = 'top-center', offset = 16, ...rest }: ToasterProps) {
  return (
    <SonnerToaster
      position={position}
      offset={offset}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: toastClasses,
          success: successToastClasses,
          title: titleClasses,
          description: descriptionClasses,
          icon: iconClasses,
        },
      }}
      {...rest}
    />
  );
}

export { toast };
